class TTCustomAccordion {
  constructor(container) {
    this.container = container;
    this.items = Array.from(container.querySelectorAll('[data-tt-accordion-item]'));

    if (!this.items.length) return;

    this.onItemToggle = this.onItemToggle.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);

    this.items.forEach((item) => {
      item.addEventListener('toggle', this.onItemToggle);
    });

    document.addEventListener('shopify:block:select', this.onBlockSelect);

    const initialOpenItem = this.items.find((item) => item.open) || this.items[0];
    if (initialOpenItem) {
      initialOpenItem.open = true;
      this.closeOthers(initialOpenItem);
      this.applyActiveBackground(initialOpenItem);
      this.syncTriggerStates();
    }
  }

  destroy() {
    if (!this.items) return;

    this.items.forEach((item) => {
      item.removeEventListener('toggle', this.onItemToggle);
    });

    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.container.style.removeProperty('--tt-tabs-bg-color-active');
  }

  onItemToggle(event) {
    const currentItem = event.currentTarget;
    if (!currentItem.open) return;
    const currentTrigger = currentItem.querySelector('.tt-custom-tab__accordion-trigger');
    const triggerTopBeforeClose = currentTrigger?.getBoundingClientRect().top;

    this.closeOthers(currentItem);
    this.preserveViewportPosition(currentTrigger, triggerTopBeforeClose);
    this.applyActiveBackground(currentItem);
    this.syncTriggerStates();
  }

  onBlockSelect(event) {
    const selectedElement = event.target.closest('[data-tt-block-id]');
    if (!selectedElement || !this.container.contains(selectedElement)) return;

    const accordionItem = selectedElement.closest('[data-tt-accordion-item]');
    if (!accordionItem) return;

    accordionItem.open = true;
    this.closeOthers(accordionItem);
    this.applyActiveBackground(accordionItem);
    this.syncTriggerStates();

    const accordionTrigger = accordionItem.querySelector('.tt-custom-tab__accordion-trigger');
    if (!this.isElementInViewport(accordionTrigger || accordionItem)) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      accordionItem.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'nearest'
      });
    }
  }

  closeOthers(activeItem) {
    this.items.forEach((item) => {
      if (item !== activeItem) {
        item.open = false;
      }
    });
  }

  applyActiveBackground(activeItem) {
    const activeItemBg = (activeItem?.dataset.ttItemBgColor || '').trim();

    if (activeItemBg) {
      this.container.style.setProperty('--tt-tabs-bg-color-active', activeItemBg);
    } else {
      this.container.style.removeProperty('--tt-tabs-bg-color-active');
    }
  }

  syncTriggerStates() {
    this.items.forEach((item) => {
      const trigger = item.querySelector('.tt-custom-tab__accordion-trigger');
      if (!trigger) return;

      trigger.setAttribute('aria-expanded', item.open ? 'true' : 'false');
    });
  }

  preserveViewportPosition(trigger, topBeforeClose) {
    if (!trigger || typeof topBeforeClose !== 'number') return;

    const topAfterClose = trigger.getBoundingClientRect().top;
    const verticalShift = topAfterClose - topBeforeClose;

    if (Math.abs(verticalShift) > 1) {
      window.scrollBy({ top: verticalShift, left: 0, behavior: 'auto' });
    }
  }

  isElementInViewport(element) {
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    return rect.bottom > 0 && rect.top < viewportHeight;
  }
}

const ttCustomTabInstances = new WeakMap();

function initTTCustomTabs(root = document) {
  const scope = root instanceof Element || root instanceof Document ? root : document;
  const components = scope.querySelectorAll('.tt-custom-tab__component');

  components.forEach((component) => {
    if (ttCustomTabInstances.has(component)) return;

    const instance = new TTCustomAccordion(component);
    ttCustomTabInstances.set(component, instance);
  });
}

function destroyTTCustomTabs(root = document) {
  const scope = root instanceof Element || root instanceof Document ? root : document;
  const components = scope.querySelectorAll('.tt-custom-tab__component');

  components.forEach((component) => {
    const instance = ttCustomTabInstances.get(component);
    if (!instance) return;

    instance.destroy();
    ttCustomTabInstances.delete(component);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initTTCustomTabs());
} else {
  initTTCustomTabs();
}

document.addEventListener('shopify:section:load', (event) => {
  initTTCustomTabs(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyTTCustomTabs(event.target);
});
